/* sc-cart-drawer-row-order.js — keep drawer item rows visually NEWEST-FIRST
 * (owner 2026-08-19, anh tdnt): the most recently added product sits on top.
 *
 * The Shipping Protection tick pipeline must remove + re-add the parent line
 * bundled with its child (Shopify only creates native parent_relationship
 * nesting when both arrive in one add call), which moves that line to the
 * top of the cart. The server order cannot be restored (no reorder API), so
 * the drawer pins each row's visual position instead: first sighting of a
 * line key assigns it a DESCENDING sequence number (newest sighting gets the
 * smallest flex `order`, so it renders first; persisted per tab), and every
 * re-render re-applies it before paint. A remove+re-add of the SAME key
 * (upsell bundle flight) keeps its position. Checkout still shows the server
 * order — accepted trade-off (merchant 2026-07-17).
 *
 * Requires .sc-cart-v4 .cart-items { display:flex; flex-direction:column }
 * from sc-cart-mvp.css. Rows without a data-cart-item-key are left alone.
 */
(function () {
  'use strict';

  // Both cart surfaces load this file through their own injection snippet —
  // installing twice would double every observer.
  if (window.__scCartRowOrderInstalled) return;
  window.__scCartRowOrderInstalled = true;

  var KEY = 'sc-cart-row-order';
  var map = {};
  // Newest-first: every newly sighted key takes a value BELOW the current
  // minimum. Sessions that still carry the old ascending map keep working —
  // their rows hold position and anything added next lands on top of them.
  var lowest = 0;

  try { map = JSON.parse(sessionStorage.getItem(KEY)) || {}; } catch (e) { map = {}; }
  Object.keys(map).forEach(function (k) {
    if (map[k] <= lowest) lowest = map[k];
  });

  function persist() {
    try { sessionStorage.setItem(KEY, JSON.stringify(map)); } catch (e) { /* per-tab nicety only */ }
  }

  /* Pin through a stylesheet, NOT inline style: the Customily embed patches
   * the drawer rows about once a second and wipes their style attribute —
   * with inline pinning the rows snapped back to server order and were
   * re-pinned next frame, visibly swapping positions twice a second (the
   * drawer "blink", proved via style-mutation log on LIVE 2026-07-17). A
   * keyed rule in our own <style> is out of that patch's reach. */
  var styleEl = null;
  function cssEscape(value) {
    return (window.CSS && CSS.escape) ? CSS.escape(value) : value.replace(/["\\]/g, '\\$&');
  }
  function applyOrderRules(rows) {
    var rules = '';
    for (var i = 0; i < rows.length; i++) {
      var k = rows[i].getAttribute('data-cart-item-key');
      if (!k || !(k in map)) continue;
      var esc = cssEscape(k);
      rules += 'cart-drawer .cart-items > .cart-drawer-item[data-cart-item-key="' + esc + '"],' +
        '.sc-cart-v4-page .cart-items tbody > tr.cart-item[data-cart-item-key="' + esc + '"]' +
        '{order:' + map[k] + ';}\n';
    }
    if (!styleEl || !styleEl.isConnected) {
      styleEl = document.getElementById('sc-cart-row-order-style');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'sc-cart-row-order-style';
        (document.head || document.documentElement).appendChild(styleEl);
      }
    }
    if (styleEl.textContent !== rules) styleEl.textContent = rules;
  }

  function apply() {
    // Both surfaces share one sequence map so the drawer and the cart page
    // always agree on row positions. Drawer rows are flex items of
    // .cart-items (ul); page rows are flex items of the V4 tbody
    // (sc-cart-page.css flattens the table and makes tbody a flex column).
    var rows = document.querySelectorAll(
      'cart-drawer .cart-items > .cart-drawer-item, .sc-cart-v4-page .cart-items tbody > tr.cart-item'
    );
    if (!rows.length) return;
    var changed = false;
    // REVERSE DOM order for a multi-row first sighting: Shopify lists the
    // newest line first, and each new key takes a smaller number than the
    // one before it — walking backwards hands the DOM-first (newest) row the
    // smallest order of the batch, so top-to-bottom reads newest → oldest.
    for (var i = rows.length - 1; i >= 0; i--) {
      var k = rows[i].getAttribute('data-cart-item-key');
      if (!k) continue;
      if (!(k in map)) {
        map[k] = --lowest;
        changed = true;
      }
    }
    if (changed) persist();
    // Never write inline style.order here — the Customily embed wipes the
    // rows' style attribute about once a second, and re-pinning inline made
    // the rows visibly swap positions on every wipe. The keyed stylesheet is
    // the single order authority.
    applyOrderRules(rows);

    // Mark the VISUALLY-first card per container — CSS sibling selectors see
    // DOM order, so the cart page's dashed separators need this class to
    // know which card sits on top after pinning.
    var containers = [];
    var winners = [];
    for (var c = 0; c < rows.length; c++) {
      var p = rows[c].parentNode;
      var ck = rows[c].getAttribute('data-cart-item-key');
      var ord = (ck && map[ck]) || 0;
      var ci = containers.indexOf(p);
      if (ci === -1) {
        containers.push(p);
        winners.push({ row: rows[c], ord: ord });
      } else if (ord < winners[ci].ord) {
        winners[ci] = { row: rows[c], ord: ord };
      }
    }
    // Only touch classList when the state actually changes — apply() runs on
    // every DOM tick (countdown text mutates each second) and a blind
    // remove+add pair emits two attribute mutations per second that feed
    // every attribute observer on the page (third-party app scripts).
    var winnerRows = [];
    for (var w = 0; w < winners.length; w++) winnerRows.push(winners[w].row);
    for (var d = 0; d < rows.length; d++) {
      var shouldBeFirst = winnerRows.indexOf(rows[d]) !== -1;
      if (shouldBeFirst !== rows[d].classList.contains('sc-visual-first')) {
        rows[d].classList.toggle('sc-visual-first', shouldBeFirst);
      }
    }

    // Cart page H1 count lives OUTSIDE .js-contents, so cart mutations never
    // re-render it — keep it live from the merchandise rows (placeholder
    // rows carry no .cart-item class and are excluded).
    var countEl = document.querySelector('[data-sc-cart-count]');
    if (countEl) {
      var sum = 0;
      var pageRows = document.querySelectorAll('.sc-cart-v4-page .cart-items tbody > tr.cart-item');
      for (var j = 0; j < pageRows.length; j++) {
        sum += parseInt(pageRows[j].getAttribute('data-quantity'), 10) || 0;
      }
      var text = String(sum);
      if (pageRows.length && countEl.textContent !== text) countEl.textContent = text;
    }
  }

  /* Re-apply before the next frame after any re-render — same pre-paint
     pattern as sc-cart-expire-timer, so rows never flash in server order. */
  var scheduled = false;
  new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      apply();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* Synchronous hook for sc-cart-protection-toggle's swapSections: rows must
     be re-pinned BEFORE it restores the scroller position, or the restore
     lands against server-order layout and scroll anchoring drags the list. */
  window.scCartRowOrder = { apply: apply };

  apply();
})();
